import { useEffect, useState } from 'react';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import { ProfileService } from '../services/profile';
import { APP_LANGUAGES, type AppLanguage, GENDERS, type Gender, type NotificationSettings, type PersonalData, UNIT_SYSTEMS, type UnitSystem, type UpdateProfileRequest, type UserProfile } from '../types/profile';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  countryCode?: string;
  phoneCode?: string;
  timezone?: string;
  general?: string;
}

export function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<'personal' | 'preferences' | 'notifications' | 'security'>('personal');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [personalData, setPersonalData] = useState<Partial<PersonalData>>({});
  const [appLanguage, setAppLanguage] = useState<AppLanguage>('en');
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');
  const [timezone, setTimezone] = useState('');
  const [notifications, setNotifications] = useState<Partial<NotificationSettings>>({});

  useEffect(() => {
    void loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileData = await ProfileService.getProfile();
      setProfile(profileData);
      
      // Initialize form state
      setPersonalData(profileData.personalData ?? {});
      setAppLanguage((profileData.appLanguage as AppLanguage) ?? 'en');
      setUnitSystem((profileData.unitSystem as UnitSystem) ?? 'metric');
      setTimezone(profileData.timezone ?? '');
      setNotifications(profileData.notifications ?? {});
      
      setErrors({});
    } catch (_error) {
      setErrors({ general: 'Failed to load profile information' });
    } finally {
      setLoading(false);
    }
  };

  const validatePersonalData = (): boolean => {
    const newErrors: FormErrors = {};

    if (personalData.firstName && personalData.firstName.trim().length < 1) {
      newErrors.firstName = 'First name is required';
    }

    if (personalData.lastName && personalData.lastName.trim().length < 1) {
      newErrors.lastName = 'Last name is required';
    }

    if (personalData.dateOfBirth && !ProfileService.validateDateOfBirth(personalData.dateOfBirth)) {
      newErrors.dateOfBirth = 'Invalid date format or date in future';
    }

    if (personalData.phoneNumber && !ProfileService.validatePhoneNumber(personalData.phoneNumber)) {
      newErrors.phoneNumber = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveProfile = async () => {
    if (!validatePersonalData()) {
      return;
    }

    try {
      setSaving(true);
      
      const updates: UpdateProfileRequest = {};
      
      if (activeTab === 'personal' && personalData) {
        updates.personalData = personalData as PersonalData;
      }
      
      if (activeTab === 'preferences') {
        updates.appLanguage = appLanguage;
        updates.unitSystem = unitSystem;
      }
      
      if (activeTab === 'notifications' && notifications) {
        updates.notifications = notifications;
      }

      let updatedProfile: UserProfile;
      
      if (activeTab === 'security' && timezone) {
        updatedProfile = await ProfileService.updateTimezone(timezone);
      } else {
        updatedProfile = await ProfileService.updateProfile(updates);
      }
      
      setProfile(updatedProfile);
      setErrors({});
      setSuccessMessage('Profile updated successfully!');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (_error) {
      setErrors({ general: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async () => {
    try {
      setDeleting(true);
      await ProfileService.deleteProfile();
      
      // Clear auth and redirect to login
      localStorage.removeItem('treel_token');
      window.location.href = '/login';
      
    } catch (_error) {
      setErrors({ general: 'Failed to delete profile' });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-bg-secondary rounded-xl border border-border-light p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-action-key-default mx-auto mb-4" />
            <p className="text-text-muted">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: '👤' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'security', label: 'Security', icon: '🔒' }
  ] as const;

  return (
    <div className="flex-1 overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">Profile Settings</h1>
          <p className="text-text-muted">Manage your account information and preferences</p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-danger-light border border-danger-border rounded-lg">
            <p className="text-danger-text">{errors.general}</p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-success-light border border-success-border rounded-lg">
            <p className="text-success-text">{successMessage}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-border-light">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-action-key-default text-action-key-default'
                      : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-bg-secondary rounded-xl border border-border-light p-8">
          {/* Personal Info Tab */}
          {activeTab === 'personal' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Personal Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    First Name
                  </label>
                  <Input
                    value={personalData.firstName ?? ''}
                    onChange={(e) => setPersonalData(prev => ({ ...prev, firstName: e.target.value }))}
                    error={!!errors.firstName}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-danger-text">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Last Name
                  </label>
                  <Input
                    value={personalData.lastName ?? ''}
                    onChange={(e) => setPersonalData(prev => ({ ...prev, lastName: e.target.value }))}
                    error={!!errors.lastName}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-danger-text">{errors.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Email
                  </label>
                  <Input
                    value={profile?.email ?? ''}
                    disabled
                    className="bg-disabled-fill"
                  />
                  <p className="mt-1 text-xs text-text-muted">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Date of Birth
                  </label>
                  <Input
                    type="date"
                    value={personalData.dateOfBirth ?? ''}
                    onChange={(e) => setPersonalData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    error={!!errors.dateOfBirth}
                  />
                  {errors.dateOfBirth && (
                    <p className="mt-1 text-sm text-danger-text">{errors.dateOfBirth}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Gender
                  </label>
                  <select
                    value={personalData.gender ?? ''}
                    onChange={(e) => setPersonalData(prev => ({ ...prev, gender: e.target.value as Gender }))}
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-bg-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-action-key-default focus:border-action-key-default"
                  >
                    <option value="">Select gender</option>
                    {Object.entries(GENDERS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Phone Number
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={personalData.countryCode ?? ''}
                      onChange={(e) => setPersonalData(prev => ({ ...prev, countryCode: e.target.value }))}
                      placeholder="Country"
                    />
                    <Input
                      value={personalData.phoneCode ?? ''}
                      onChange={(e) => setPersonalData(prev => ({ ...prev, phoneCode: e.target.value }))}
                      placeholder="Code"
                    />
                    <Input
                      value={personalData.phoneNumber ?? ''}
                      onChange={(e) => setPersonalData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      error={!!errors.phoneNumber}
                      placeholder="Number"
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-danger-text">{errors.phoneNumber}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary mb-4">App Preferences</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Language
                  </label>
                  <select
                    value={appLanguage}
                    onChange={(e) => setAppLanguage(e.target.value as AppLanguage)}
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-bg-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-action-key-default focus:border-action-key-default"
                  >
                    {Object.entries(APP_LANGUAGES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Unit System
                  </label>
                  <select
                    value={unitSystem}
                    onChange={(e) => setUnitSystem(e.target.value as UnitSystem)}
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-bg-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-action-key-default focus:border-action-key-default"
                  >
                    {Object.entries(UNIT_SYSTEMS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Notification Settings</h2>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="notifications-enabled"
                    checked={notifications.isEnabled ?? false}
                    onChange={(e) => setNotifications(prev => ({ ...prev, isEnabled: e.target.checked }))}
                    className="h-4 w-4 text-action-key-default focus:ring-action-key-default border-border-medium rounded"
                  />
                  <label htmlFor="notifications-enabled" className="ml-2 block text-sm text-text-primary">
                    Enable notifications
                  </label>
                </div>

                {notifications.isEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Start Time
                      </label>
                      <Input
                        type="time"
                        value={notifications.startTime ? ProfileService.formatTime(notifications.startTime.hour, notifications.startTime.minute) : '09:00'}
                        onChange={(e) => {
                          const time = ProfileService.parseTime(e.target.value);
                          setNotifications(prev => ({ ...prev, startTime: time }));
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        End Time
                      </label>
                      <Input
                        type="time"
                        value={notifications.endTime ? ProfileService.formatTime(notifications.endTime.hour, notifications.endTime.minute) : '21:00'}
                        onChange={(e) => {
                          const time = ProfileService.parseTime(e.target.value);
                          setNotifications(prev => ({ ...prev, endTime: time }));
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Security Settings</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Timezone
                  </label>
                  <Input
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    placeholder="e.g., America/New_York"
                  />
                  <p className="mt-1 text-xs text-text-muted">
                    Enter your timezone identifier (e.g., America/New_York, Europe/London)
                  </p>
                </div>

                <div className="border-t border-border-light pt-6">
                  <h3 className="text-lg font-medium text-text-primary mb-4">Danger Zone</h3>
                  <div className="bg-danger-light p-4 rounded-lg border border-danger-border">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-sm font-medium text-danger-text mb-1">Delete Account</h4>
                        <p className="text-sm text-danger-text">
                          Permanently delete your account and all associated data. This action cannot be undone.
                        </p>
                      </div>
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="ml-4 text-danger-text hover:bg-danger-light"
                      >
                        Delete Account
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-border-light">
            <Button
              variant="secondary"
              onClick={() => void loadProfile()}
              disabled={saving}
            >
              Reset Changes
            </Button>
            <Button
              variant="primary"
              onClick={saveProfile}
              disabled={saving}
              isLoading={saving}
            >
              Save Changes
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-bg-primary rounded-lg p-6 max-w-md w-full mx-4 border border-border-light">
              <h3 className="text-lg font-medium text-text-primary mb-4">Delete Account</h3>
              <p className="text-text-muted mb-6">
                Are you sure you want to delete your account? This will permanently remove all your data and cannot be undone.
              </p>
              <div className="flex space-x-4">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={deleteProfile}
                  disabled={deleting}
                  isLoading={deleting}
                  fullWidth
                  className="bg-danger-border hover:bg-danger-text"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}